import React, { useState } from 'react';

import { WarningFilled } from '@ant-design/icons';
import { Modal as ModalAntD, Tag } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { scopeLabel } from './integrationLabels';

import { IntegrationTokenCreated } from '../../../../types/responses/Interoperability';

/** O @types do antd 4.10 não declara children no Modal; mesmo contorno do resto do módulo. */
const Modal: any = ModalAntD;

const Warning = styled.div`
  display: flex;
  gap: 10px;
  background: #fff8e6;
  border: 1px solid #f0d9a0;
  border-radius: 3px;
  padding: 12px 14px;
  color: #7a5200;
  font-size: 13px;
  line-height: 1.5;
`;

const TokenBox = styled.code`
  display: block;
  background: #f6f8fa;
  border: 1px solid #e3e6ea;
  border-radius: 3px;
  padding: 12px 14px;
  margin-top: 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  color: #12263f;
  word-break: break-all;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const CopyButton = styled.button<{ copied: boolean }>`
  background: ${({ copied, theme }) => (copied ? '#e7f6ed' : theme.primary)};
  color: ${({ copied }) => (copied ? '#1e7a45' : '#fff')};
  border: 1px solid ${({ copied, theme }) => (copied ? '#bfe6cf' : theme.primary)};
  border-radius: 3px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
`;

const Summary = styled.div`
  margin-top: 16px;
  font-size: 13px;
  color: #5b6472;
  line-height: 1.6;
`;

const Label = styled.span`
  color: #8d929e;
  margin-right: 6px;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Pending = styled.span`
  flex: 1;
  color: #b02525;
  font-size: 13px;
`;

const Done = styled.span`
  flex: 1;
  color: #1e7a45;
  font-size: 13px;
`;

const CloseButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 3px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
`;

export type IntegrationTokenCreatedModalProps = {
  created: IntegrationTokenCreated;
  onClose: () => void;
};

/**
 * O token recém-criado, exibido uma única vez.
 *
 * Mesma regra da tela de token de convite (F4): o valor em claro só existe nesta resposta,
 * e não há endpoint que o recupere. Se a pessoa fechar sem copiar, a única saída é revogar
 * e criar outro — e é por isso que o rodapé insiste antes de a cópia acontecer.
 *
 * O token não vai para URL, storage nem console em nenhum caminho desta tela.
 */
export const IntegrationTokenCreatedModal: React.FC<IntegrationTokenCreatedModalProps> = ({
  created,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch {
      // Navegador sem permissão de área de transferência: o token continua na tela para
      // seleção manual, então não há o que avisar além de não confirmar a cópia.
      setCopied(false);
    }
  };

  return (
    <Modal
      visible
      title={T('Integration token created')}
      width={640}
      onCancel={onClose}
      footer={
        <Foot>
          {copied ? (
            <Done>{T('Token copied.')}</Done>
          ) : (
            <Pending>{T('Did you copy the token? It cannot be recovered.')}</Pending>
          )}
          <CloseButton type="button" onClick={onClose}>
            {copied ? T('Close') : T('I copied it, close')}
          </CloseButton>
        </Foot>
      }
    >
      <div>
        <Warning>
          <WarningFilled />
          <span>
            <strong>{T('This token will be shown only once.')}</strong>{' '}
            {T(
              'Send it to the integrator through a secure channel. We store only a hash — if it is lost, revoke it and create another.',
            )}
          </span>
        </Warning>

        <TokenBox>{created.token}</TokenBox>

        <Row>
          <CopyButton type="button" copied={copied} onClick={copy}>
            {copied ? T('Copied') : T('Copy token')}
          </CopyButton>
        </Row>

        <Summary>
          <div>
            <Label>{T('Integrator')}</Label>
            {created.name}
          </div>
          <div>
            <Label>{T('Scopes')}</Label>
            {created.scopes.map((scope) => (
              <Tag key={scope}>{scopeLabel(scope)}</Tag>
            ))}
          </div>
          <div>
            <Label>{T('Validity')}</Label>
            {created.expires_at
              ? new Date(created.expires_at).toLocaleString()
              : T('does not expire')}
          </div>
        </Summary>
      </div>
    </Modal>
  );
};
